import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { SKIP_LOADER } from '../interceptors/skip-loader.context';

const VERSION_URL = 'version.json';
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private http = inject(HttpClient);
  private currentVersion: string | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  /**
   * Periodically fetches version.json and reloads the page if the deployed version
   * changed (e.g. after a new deploy). Runs in both development and production so
   * testing users on dev env also get the latest code without manual refresh.
   */
  start(): void {
    this.fetchVersion()
      .then((version) => {
        this.currentVersion = version;
        this.scheduleCheck();
        this.listenForVisibilityChange();
      })
      .catch(() => {
        // Ignore initial failure (e.g. dev server without version.json)
      });
  }

  private async fetchVersion(): Promise<string> {
    const cacheBuster = `?t=${Date.now()}`;
    const context = new HttpContext().set(SKIP_LOADER, true);
    const body = await firstValueFrom(
      this.http.get<{ version: string }>(VERSION_URL + cacheBuster, { context })
    );
    return body?.version ?? '0';
  }

  private scheduleCheck(): void {
    this.intervalId = setInterval(() => this.checkAndReload(), CHECK_INTERVAL_MS);
  }

  private listenForVisibilityChange(): void {
    if (typeof document === 'undefined' || !document.addEventListener) {
      return;
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkAndReload();
      }
    });
  }

  private checkAndReload(): void {
    this.fetchVersion()
      .then((version) => {
        if (this.currentVersion !== null && version !== this.currentVersion) {
          if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
          }
          window.location.reload();
        }
      })
      .catch(() => {"Error while fetching Version"});
  }
}
